/**
 * Construit la chaîne de requête pour l'API
 * @param {Object} params - Paramètres à convertir en query string
 * @returns {string} - Query string formatée (ex: "?page=1&limit=20")
 */
export const buildQueryString = (params) => {
  if (!params || Object.keys(params).length === 0) return "";

  const filteredParams = Object.entries(params)
    .filter(([_, value]) => {
      // Garder uniquement les valeurs définies et non vides
      if (value === undefined || value === null) return false;
      if (typeof value === "string" && value.trim() === "") return false;
      if (Array.isArray(value) && value.length === 0) return false;
      return true;
    })
    .reduce((acc, [key, value]) => {
      // Formater les dates si nécessaire
      if (value instanceof Date) {
        acc[key] = formatDateForAPI(value);
      }
      // Formater les tableaux
      else if (Array.isArray(value)) {
        acc[key] = value.join(",");
      }
      // Garder les autres valeurs telles quelles
      else {
        acc[key] = value;
      }
      return acc;
    }, {});

  const queryString = new URLSearchParams(filteredParams).toString();
  return queryString ? `?${queryString}` : "";
};

/**
 * Formate une date pour l'API (YYYY-MM-DD)
 * @param {Date|string} date - Date à formater
 * @returns {string|null} - Date formatée ou null
 */
export const formatDateForAPI = (date) => {
  if (!date) return null;

  const d = new Date(date);
  if (isNaN(d.getTime())) return null;

  return d.toISOString().split("T")[0];
};

/**
 * Génère une plage de dates à partir d'une période
 * @param {string} period - today, week, month, quarter, year, custom
 * @param {string} customStart - Date de début personnalisée
 * @param {string} customEnd - Date de fin personnalisée
 * @returns {Object} - { startDate, endDate }
 */
export const getDateRangeFromPeriod = (period, customStart, customEnd) => {
  const now = new Date();
  const end = new Date(now);
  const start = new Date(now);

  switch (period) {
    case "today":
      start.setHours(0, 0, 0, 0);
      end.setHours(23, 59, 59, 999);
      break;

    case "week":
      start.setDate(start.getDate() - 7);
      break;

    case "month":
      start.setMonth(start.getMonth() - 1);
      break;

    case "quarter":
      start.setMonth(start.getMonth() - 3);
      break;

    case "year":
      start.setFullYear(start.getFullYear() - 1);
      break;

    case "custom":
      return {
        startDate:
          customStart ||
          formatDateForAPI(
            new Date(new Date().setMonth(new Date().getMonth() - 1)),
          ),
        endDate: customEnd || formatDateForAPI(new Date()),
      };

    default:
      return { startDate: null, endDate: null };
  }

  return {
    startDate: formatDateForAPI(start),
    endDate: formatDateForAPI(end),
  };
};

/**
 * Agrège les données pour les graphiques
 * @param {Array} data - Données à agréger
 * @param {string} valueKey - Clé des valeurs
 * @param {string} labelKey - Clé des labels
 * @returns {Array} - Données formatées pour les graphiques
 */
export const aggregateForChart = (data, valueKey, labelKey = "period") => {
  if (!data || !Array.isArray(data)) return [];

  return data.map((item) => ({
    label: item[labelKey],
    value: item[valueKey],
    ...item,
  }));
};

/**
 * Prépare les données pour un tableau
 * @param {Array} data - Données brutes
 * @param {Array} columns - Configuration des colonnes
 * @returns {Array} - Données formatées pour le tableau
 */
export const prepareTableData = (data, columns) => {
  if (!data || !Array.isArray(data)) return [];

  return data.map((item) => {
    const row = {};
    columns.forEach((col) => {
      if (typeof col.accessor === "function") {
        row[col.key] = col.accessor(item);
      } else {
        row[col.key] = item[col.accessor || col.key];
      }
    });
    return row;
  });
};

/**
 * Gère les erreurs API de manière uniforme
 * @param {Error} error - Erreur à traiter
 * @returns {Object} - Erreur formatée
 */
export const handleApiError = (error) => {
  console.error("API Error:", error);

  return {
    message:
      error.response?.data?.error || error.message || "Une erreur est survenue",
    status: error.response?.status,
    data: error.response?.data,
    originalError: error,
  };
};

/**
 * Télécharge un fichier depuis l'API
 * @param {Blob} data - Données du fichier
 * @param {string} filename - Nom du fichier
 */
export const downloadFile = (data, filename) => {
  const url = window.URL.createObjectURL(new Blob([data]));
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};
